import {
  ClientErrorCode,
  CollabConnectionClient,
  type ClientErrorShape,
  type CollabStateBridge,
  SerializerType,
  type CollabConnectionClientOptions,
} from '@atlassian/collab-connection-client';

import { type CanvasDomainError } from '@canvas/collaboration-utils';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import { WebSocketCodes } from '../network/websocket-codes.ts';

import {
  type Transport,
  type TransportCallbacks,
  type TransportContext,
} from './transport.ts';

/**
 * A nominal type representing the peer ID of the sender of a collab message.
 * This is sourced from `metadata.from` in the collab-connection-client update payload.
 */
export type CollabConnectionClientSender = string & {
  readonly __collabConnectionClientSender: unique symbol;
};

// TODO: import this when exposed from collab-connection-client
export enum DisconnectReason {
  CLIENT_DISCONNECT = 'CLIENT_DISCONNECT',
  SERVER_DISCONNECT = 'SERVER_DISCONNECT',
  SOCKET_CLOSED = 'SOCKET_CLOSED',
  SOCKET_ERROR = 'SOCKET_ERROR',
  SOCKET_TIMEOUT = 'SOCKET_TIMEOUT',
  UNKNOWN_DISCONNECT = 'UNKNOWN_DISCONNECT',
}

export function disconnectReasonToWebSocketCode(
  reason: DisconnectReason,
): WebSocketCodes {
  switch (reason) {
    case DisconnectReason.CLIENT_DISCONNECT:
      return WebSocketCodes.NORMAL_CLOSURE;
    case DisconnectReason.SERVER_DISCONNECT:
      return WebSocketCodes.EXCEPTION_CLOSURE;
    case DisconnectReason.SOCKET_CLOSED:
      return WebSocketCodes.NORMAL_CLOSURE;
    case DisconnectReason.SOCKET_ERROR:
      return WebSocketCodes.CLIENT_ERROR;
    case DisconnectReason.SOCKET_TIMEOUT:
      return WebSocketCodes.CLIENT_ERROR;
    case DisconnectReason.UNKNOWN_DISCONNECT:
      return WebSocketCodes.CLIENT_ERROR;
  }
}

type CanvasDomainErrorEvent = Error & {
  domainError: CanvasDomainError;
};

function createCanvasDomainErrorEvent(
  domainError: CanvasDomainError,
): CanvasDomainErrorEvent {
  const error = new Error(domainError.message) as CanvasDomainErrorEvent;
  error.name = 'CanvasDomainError';
  error.domainError = domainError;
  return error;
}

// CNS-30360: Translate SESSION_LIMIT_REACHED into SESSION_FULL (4002) so the existing
// YjsSocket.onSocketClosed path fires the 'sessionFull' UX (snackbar + read-only + retry).
const SESSION_LIMIT_CLOSE_REASON = 'session_limit_reached';

// CNS-31655: Translate DOCUMENT_LOCKED into SESSION_LOCKED (4007) so the existing
// YjsSocket.onSocketClosed path fires the 'sessionLocked' UX (snackbar + read-only
// + retry-with-reset-backoff), matching the Cloudflare relay locked-document flow.
const DOCUMENT_LOCKED_CLOSE_REASON = 'document_locked';

type ClientError = Error & Pick<ClientErrorShape, 'code' | 'status' | 'meta'>;

/**
 * Adapt a C3 ClientErrorShape into a real Error instance so it satisfies the
 * TransportCallbacks.onError(error: Error) contract. ClientErrorShape is a
 * plain object ({ code, message, ... }) with no `name`, so it is not assignable
 * to Error on its own. We preserve the structural fields (code/status/meta) on
 * the resulting Error for downstream consumers.
 */
function clientErrorShapeToError(shape: ClientErrorShape): ClientError {
  const error = new Error(shape.message) as ClientError;
  error.name = shape.code;
  error.code = shape.code;
  error.status = shape.status;
  error.meta = shape.meta;
  return error;
}

export class CollabConnectionTransport implements Transport {
  private client: CollabConnectionClient<Uint8Array> | undefined;
  private disposed = false;

  private readonly onMessageSent?: TransportCallbacks['onMessageSent'];

  constructor(
    private readonly ctx: TransportContext,
    private readonly callbacks: TransportCallbacks,
  ) {
    const observer = callbacks.onMessageSent;
    if (observer) {
      this.onMessageSent = (payload) => {
        if (!this.disposed) {
          observer(payload);
        }
      };
    }
  }

  connect(): void {
    let analyticsWebClient;
    if (!this.ctx.getAnalyticsWebClient) {
      console.error('Not passing analytics client to C3');
    } else {
      analyticsWebClient = this.ctx.getAnalyticsWebClient;
    }

    if (this.ctx.stateBridge === undefined) {
      throw new Error('C3 client initialised without a state bridge');
    }

    if (this.onMessageSent) {
      this.ctx.stateBridge.onRetrieve(this.onMessageSent);
    }

    const clientOptions: CollabConnectionClientOptions<Uint8Array> = {
      bridge: this.ctx.stateBridge as CollabStateBridge<Uint8Array<ArrayBufferLike>>,
      url: this.ctx.relayDetails.url,
      namespace: this.ctx.relayDetails.namespace,
      permissionTokenRefresh: () => this.ctx.accessTokenSupplier(),
      product: {
        product: 'ncs-canvas',
      },
      serializer: {
        type: SerializerType.JSON,
      },
      getAnalyticsWebClient: analyticsWebClient,
      throwOnNotConnected: false,
      ...(this.ctx.relayDetails.extraHeaders
        ? { extraHeaders: this.ctx.relayDetails.extraHeaders }
        : {}),
    };

    const client = new CollabConnectionClient(clientOptions);
    this.client = client;

    client.on('connected', () => {
      if (!this.disposed) {
        this.callbacks.onOpen();
      }
    });

    client.on('disconnected', (reason: DisconnectReason) => {
      if (!this.disposed) {
        const code = disconnectReasonToWebSocketCode(reason);
        this.callbacks.onClose(code, reason);
      }
    });

    client.on('error', (error: ClientErrorShape) => {
      if (this.disposed) {
        return;
      }

      // CNS-30360: SESSION_LIMIT_REACHED closes (not errors) so YjsSocket fires 'sessionFull' UX.
      if (error.code === ClientErrorCode.SESSION_LIMIT_REACHED) {
        this.callbacks.onClose(WebSocketCodes.SESSION_FULL, SESSION_LIMIT_CLOSE_REASON);
        return;
      }

      // CNS-31655: DOCUMENT_LOCKED closes (not errors) so YjsSocket fires the
      // 'sessionLocked' UX, keeping NCS behaviour identical to the Cloudflare relay.
      if (error.code === ClientErrorCode.DOCUMENT_LOCKED) {
        this.callbacks.onClose(
          WebSocketCodes.SESSION_LOCKED,
          DOCUMENT_LOCKED_CLOSE_REASON,
        );
        return;
      }

      ReliabilityMetrics.getInstance().failure(
        MetricName.COLLAB_CONNECTION_TRANSPORT_ERROR,
        {
          errorMessage: error.message,
          connectionAttempt: this.ctx.connectionAttempt,
        },
      );
      this.callbacks.onError(clientErrorShapeToError(error));
    });

    client.onDomainError<CanvasDomainError>(({ data }) => {
      if (this.disposed) {
        return;
      }

      const error = createCanvasDomainErrorEvent(data.error);
      ReliabilityMetrics.getInstance().failure(
        MetricName.COLLAB_CONNECTION_TRANSPORT_DOMAIN_ERROR,
        {
          errorMessage: error.message,
          errorType: error.domainError.type,
        },
      );
      this.callbacks.onError(error);
    });

    client.on(
      'update',
      (payload: { data: ArrayBuffer; metadata?: { from?: string } }) => {
        if (!this.disposed) {
          this.callbacks.onMessage(
            new Uint8Array(payload.data),
            payload.metadata?.from as CollabConnectionClientSender | undefined,
          );
        }
      },
    );

    client.on(
      'transient',
      (payload: { data: ArrayBuffer; metadata?: { from?: string } }) => {
        if (!this.disposed) {
          this.callbacks.onTransientMessage?.(
            new Uint8Array(payload.data),
            payload.metadata?.from as CollabConnectionClientSender | undefined,
          );
        }
      },
    );

    client.setOnAckTimeout(() => {
      if (!this.disposed) {
        this.callbacks.onAckTimeout?.();
      }
    });

    client.initialize();
  }

  // the data here isn't actually used, its just here to preserve the interface.
  // if c3 is set up properly with the statebridge, then sendThenConfirm here should automatically
  // retrieve the batch from the StateBridge object
  send(_data: Uint8Array<ArrayBufferLike>): void {
    this.client?.sendThenConfirm();
    return;
  }

  sendToPeer(data: Uint8Array<ArrayBufferLike>, peer: string): void {
    this.client?.sendToPeer({ name: 'transient', payload: data }, peer);
  }

  sendAwareness(data: Uint8Array<ArrayBufferLike>): void {
    this.client?.send({ name: 'transient', payload: data });
  }

  flush(ignoreAck?: boolean) {
    this.client?.flush(ignoreAck);
  }

  close(): void {
    this.client?.destroy();
  }

  dispose(): void {
    this.disposed = true;
    if (this.onMessageSent) {
      this.ctx.stateBridge?.offRetrieve(this.onMessageSent);
    }
    this.client?.destroy();
    this.client = undefined;
  }
}

export function createCollabConnectionTransport(
  ctx: TransportContext,
  callbacks: TransportCallbacks,
): Transport {
  const transport = new CollabConnectionTransport(ctx, callbacks);
  transport.connect();
  return transport;
}
